import React, { Component, ReactNode } from 'react';
import { logger } from 'utils/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Route-level ErrorBoundary. Prevents a single component throw from taking down
// the whole app, and routes the error through our logger util so it can later
// be forwarded to an external error monitor.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    logger.error('React tree crashed:', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const message = this.state.error?.message ?? 'Unknown error';
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          background: '#0F172B',
          color: '#E2E8F0',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: '480px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '12px' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: '15px', color: '#90A1B9', marginBottom: '24px', lineHeight: 1.5 }}>
            The page failed to render. This error has been logged. Try reloading the page or
            returning to the home page.
          </p>
          {process.env.NODE_ENV !== 'production' && (
            <pre
              style={{
                fontSize: '12px',
                background: '#1D293D',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'left',
                overflowX: 'auto',
                marginBottom: '24px',
                color: '#FCA5A5',
              }}
            >
              {message}
            </pre>
          )}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                background: 'transparent',
                color: '#E2E8F0',
                border: '1px solid #314158',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
